import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';

type DepthRow = { price: string; size: string; total: number };

const ASKS: ReadonlyArray<DepthRow> = [
  { price: '0.66', size: '12,400', total: 12400 },
  { price: '0.65', size: '8,210', total: 20610 },
  { price: '0.64', size: '4,800', total: 25410 },
  { price: '0.63', size: '2,100', total: 27510 },
];
const BIDS: ReadonlyArray<DepthRow> = [
  { price: '0.62', size: '3,200', total: 3200 },
  { price: '0.61', size: '6,400', total: 9600 },
  { price: '0.60', size: '11,200', total: 20800 },
  { price: '0.59', size: '5,800', total: 26600 },
];
const ASK_MAX = 27510;
const BID_MAX = 26600;

function depthPct(total: number, max: number): number {
  return (total / max) * 100;
}

export default function Book(): JSX.Element {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  return (
    <section id="orderbook" className="border-rule border-b py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-6">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-8">
          <div>
            <div className="text-dim mb-[18px] font-mono text-[11px] tracking-[0.22em] uppercase">
              {t('book.eyebrow')}
            </div>
            <h2 className="m-0 max-w-[800px] font-sans text-[clamp(40px,5vw,72px)] leading-none font-semibold tracking-[-0.03em]">
              {t('book.headlineLine1')}
              <br />
              {t('book.headlineLine2')}
            </h2>
          </div>
          <div className="border-rule text-dim border-l pl-5 text-right font-mono text-[11px] tracking-[0.18em] uppercase">
            {t('book.metaLine1')}
            <br />
            {t('book.metaLine2')}
            <br />
            {t('book.metaLine3')}
          </div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={reduce ? { duration: 0 } : { duration: 0.5 }}
          className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16"
        >
          <div>
            <div className="text-dim mb-3 font-mono text-[11px] tracking-[0.18em] uppercase">
              {t('book.label')}
            </div>
            <div className="rounded-card border-rule bg-paper border p-5 font-mono text-[13px]">
              <div className="border-rule text-dim mb-2 grid grid-cols-[60px_1fr_80px] gap-3 border-b pb-2 text-[10px] tracking-[0.18em] uppercase">
                <span>{t('book.head.price')}</span>
                <span>{t('book.head.size')}</span>
                <span className="text-right">{t('book.head.total')}</span>
              </div>
              {ASKS.slice()
                .reverse()
                .map((r) => (
                  <div
                    key={`a-${r.price}`}
                    className="relative grid grid-cols-[60px_1fr_80px] items-center gap-3 py-1.5"
                  >
                    <div
                      className="bg-orange/10 absolute inset-0 -z-10"
                      style={{ width: `${depthPct(r.total, ASK_MAX)}%` }}
                      aria-hidden="true"
                    />
                    <span className="text-orange">{r.price}</span>
                    <span>{r.size}</span>
                    <span className="text-right opacity-70">{r.total.toLocaleString('en-US')}</span>
                  </div>
                ))}
              <div className="border-rule text-dim my-3 border-y py-2 text-center text-[10px] tracking-[0.18em] uppercase">
                {t('book.spread')}
              </div>
              {BIDS.map((r) => (
                <div
                  key={`b-${r.price}`}
                  className="relative grid grid-cols-[60px_1fr_80px] items-center gap-3 py-1.5"
                >
                  <div
                    className="bg-green/10 absolute inset-0 -z-10"
                    style={{ width: `${depthPct(r.total, BID_MAX)}%` }}
                    aria-hidden="true"
                  />
                  <span className="text-green">{r.price}</span>
                  <span>{r.size}</span>
                  <span className="text-right opacity-70">{r.total.toLocaleString('en-US')}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-dim mb-3 font-mono text-[11px] tracking-[0.18em] uppercase">
              {t('book.explainerHeading')}
            </div>
            <p className="text-ink m-0 max-w-[480px] text-[22px] leading-[1.4] tracking-[-0.015em]">
              {t('book.explainer')}
            </p>
            <div className="border-rule mt-8 flex flex-wrap gap-8 border-t pt-6">
              <div>
                <div className="text-dim font-mono text-[11px] uppercase">
                  {t('book.stats.spreadLabel')}
                </div>
                <div className="mt-1.5 font-mono text-[32px] font-bold">
                  {t('book.stats.spreadValue')}
                </div>
              </div>
              <div>
                <div className="text-dim font-mono text-[11px] uppercase">
                  {t('book.stats.feeLabel')}
                </div>
                <div className="mt-1.5 font-mono text-[32px] font-bold">
                  {t('book.stats.feeValue')}
                </div>
              </div>
              <div>
                <div className="text-dim font-mono text-[11px] uppercase">
                  {t('book.stats.exitLabel')}
                </div>
                <div className="text-orange mt-1.5 font-mono text-[32px] font-bold">
                  {t('book.stats.exitValue')}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
